import { useState, useEffect, useCallback } from 'react';

import {
  zmianyDoAkceptacji,
  type PotwierdzZmiany,
} from '../components/cennik/PotwierdzenieZmianyCennika';
import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

// ─── Types ───────────────────────────────────────────────────────────────────

export type BillingPlan = 'free' | 'starter' | 'professional' | 'enterprise';

export interface StripeSubscription {
  id: string;
  organization_id: string;
  stripe_subscription_id: string;
  stripe_price_id: string;
  plan: BillingPlan;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  /** Zaplanowana obniżka od następnego okresu (runda 4, harmonogram Stripe). */
  zmiana_planu_na?: string | null;
  zmiana_planu_okres?: string | null;
  zmiana_planu_od?: string | null;
  /** Plan sprzed zaplanowanej zmiany (runda 5). */
  zmiana_planu_z?: string | null;
  created_at: string;
  updated_at: string;
}

export interface StripeInvoice {
  id: string;
  organization_id: string;
  stripe_invoice_id: string;
  stripe_subscription_id: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  invoice_url: string | null;
  pdf_url: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  fakturownia_invoice_id: number | null;
  fakturownia_invoice_number: string | null;
  fakturownia_invoice_url: string | null;
  /** Numer KSeF dokumentu VAT (860) — wpisuje go webhook albo dozór `ksef-faktur`. */
  ksef_numer?: string | null;
  ksef_data?: string | null;
}

export interface HardwareOrder {
  id: string;
  organization_id: string;
  shopify_order_id: string | null;
  shopify_order_number: string | null;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  items: Array<{
    title: string;
    quantity: number;
    price: string;
    sku: string;
    variant_title?: string | null;
  }>;
  total_price: number;
  currency: string;
  shipping_address: Record<string, unknown> | null;
  tracking_number: string | null;
  tracking_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface ModuleSubscriptionInfo {
  id: string;
  organization_id: string;
  module_id: string;
  status: 'trial' | 'active' | 'suspended' | 'cancelled';
  plan: string;
  trial_ends_at: string | null;
  subscription_starts_at: string | null;
  subscription_ends_at: string | null;
  created_at: string;
  updated_at: string;
  modules?: {
    name: string;
    description: string;
    category: string;
    icon: string;
  };
}

export interface UseBillingResult {
  // System Subscription
  subscription: StripeSubscription | null;
  currentPlan: BillingPlan;
  isSubscriptionLoading: boolean;
  hasActiveSystemSubscription: boolean;

  // Module Subscriptions
  moduleSubscriptions: ModuleSubscriptionInfo[];
  isModuleSubscriptionsLoading: boolean;

  // Invoices
  invoices: StripeInvoice[];
  isInvoicesLoading: boolean;

  // Hardware orders
  orders: HardwareOrder[];
  isOrdersLoading: boolean;

  // Error
  error: string | null;

  /**
   * Organizacja ma klienta Stripe (runda 5). Przycisk „Portal płatności" ma
   * stać także wtedy, gdy subskrypcja jest nieopłacona (`unpaid`), wstrzymana
   * albo zaplanowana do końca — odmowy zakupu 409 kierują właśnie tam,
   * a przycisk był widoczny wyłącznie przy subskrypcji aktywnej.
   */
  maKlientaStripe: boolean;

  // Actions
  /** `potwierdz` — okno ogłoszonej zmiany cennika (875); `null` także, gdy klient anulował. */
  createCheckoutSession: (
    plan: BillingPlan,
    billingInterval?: 'monthly' | 'yearly',
    potwierdz?: PotwierdzZmiany
  ) => Promise<string | null>;
  /** Odmowa serwera (np. 409 przy żywej subskrypcji) ODRZUCA obietnicę z jego zdaniem. */
  createModuleCheckoutSession: (params: {
    moduleId: string;
    tier: string;
    billingType?: string;
    currency?: string;
    quantity?: number;
    potwierdz?: PotwierdzZmiany;
  }) => Promise<string | null>;
  /** Zmiana liczby serwerów modułu ilościowego (D10, proracja Stripe). */
  changeModuleQuantity: (params: {
    moduleId: string;
    quantity: number;
  }) => Promise<{ stan: string; ilosc: number; poprzednio: number }>;
  /** Kasa Stripe na sprzęt z Marketplace. Wysyła slug i ilość, nigdy kwotę. */
  createDeviceCheckoutSession: (
    items: Array<{ slug: string; quantity: number }>
  ) => Promise<string | null>;
  openBillingPortal: () => Promise<string | null>;
  /**
   * Odwołaj zaplanowaną zmianę planu (obniżkę od następnego okresu) — modułu
   * albo, bez `moduleId`, abonamentu. Odmowa serwera ODRZUCA obietnicę z jego zdaniem.
   */
  cancelScheduledChange: (moduleId?: string) => Promise<void>;
  syncSubscription: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_NEUROSENSE_API_URL || 'https://api.insolitum.ai';

/** Odmowa API z treścią odpowiedzi — zakup czyta z niej listę zmian cennika (875). */
export class BladApi extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown
  ) {
    super(message);
    this.name = 'BladApi';
  }
}

/**
 * Wysyłka zakupu z potwierdzeniem ogłoszonej zmiany cennika: odmowa 409
 * `zmiana_cennika_do_akceptacji` pokazuje okno, a „Akceptuję” ponawia żądanie
 * z identyfikatorami zmian. `null` = klient anulował.
 */
export async function zPotwierdzeniemZmian<T>(
  wyslij: (akceptuje?: string[]) => Promise<T>,
  potwierdz?: PotwierdzZmiany
): Promise<T | null> {
  try {
    return await wyslij();
  } catch (e) {
    const zmiany = e instanceof BladApi ? zmianyDoAkceptacji(e.body) : null;
    if (!zmiany || !potwierdz) throw e;
    if (!(await potwierdz(zmiany))) return null;
    return wyslij(zmiany.map((z) => z.zmiana_id));
  }
}

async function apiFetch<T>(
  path: string,
  organizationId: string,
  options?: RequestInit
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;

  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-Tenant-Id': organizationId,
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new BladApi(
      body.message || body.error || `API error: ${response.status}`,
      response.status,
      body
    );
  }

  return response.json() as Promise<T>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Hook for billing management: subscriptions, invoices, and hardware orders.
 *
 * Uses Supabase for reading billing data (via RLS) and the neurosense-core API
 * for creating Stripe Checkout and Customer Portal sessions.
 *
 * @example
 * const {
 *   subscription,
 *   currentPlan,
 *   invoices,
 *   orders,
 *   createCheckoutSession,
 *   openBillingPortal,
 * } = useBilling();
 *
 * // Upgrade to professional
 * const url = await createCheckoutSession('professional');
 * if (url) window.location.href = url;
 *
 * // Open self-service portal
 * const portalUrl = await openBillingPortal();
 * if (portalUrl) window.location.href = portalUrl;
 */
export function useBilling(): UseBillingResult {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.organization_id || null;

  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [moduleSubscriptions, setModuleSubscriptions] = useState<ModuleSubscriptionInfo[]>([]);
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [orders, setOrders] = useState<HardwareOrder[]>([]);
  const [maKlientaStripe, setMaKlientaStripe] = useState(false);

  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [isModuleSubscriptionsLoading, setIsModuleSubscriptionsLoading] = useState(true);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(true);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch subscription ───────────────────────────────────────────────────

  const fetchSubscription = useCallback(async () => {
    if (!organizationId) {
      setSubscription(null);
      setIsSubscriptionLoading(false);
      return;
    }

    try {
      setIsSubscriptionLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('organization_id', organizationId)
        .in('status', ['active', 'trialing', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setSubscription(data || null);

      // Klient Stripe (RLS: właściciel/admin widzi swój wiersz, 053).
      const { data: klient } = await supabase
        .from('stripe_customers')
        .select('stripe_customer_id')
        .eq('organization_id', organizationId)
        .maybeSingle();
      setMaKlientaStripe(Boolean(klient?.stripe_customer_id));
    } catch (err) {
      console.error('[useBilling] Error fetching subscription:', err);
      setError('Failed to fetch subscription');
      setSubscription(null);
    } finally {
      setIsSubscriptionLoading(false);
    }
  }, [organizationId]);

  // ── Fetch invoices ─────────────────────────────────────────────────────────

  const fetchInvoices = useCallback(async () => {
    if (!organizationId) {
      setInvoices([]);
      setIsInvoicesLoading(false);
      return;
    }

    try {
      setIsInvoicesLoading(true);

      const { data, error: fetchError } = await supabase
        .from('stripe_invoices')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (fetchError) throw fetchError;

      setInvoices(data || []);
    } catch (err) {
      console.error('[useBilling] Error fetching invoices:', err);
      setError('Failed to fetch invoices');
      setInvoices([]);
    } finally {
      setIsInvoicesLoading(false);
    }
  }, [organizationId]);

  // ── Fetch hardware orders ──────────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    if (!organizationId) {
      setOrders([]);
      setIsOrdersLoading(false);
      return;
    }

    try {
      setIsOrdersLoading(true);

      const { data, error: fetchError } = await supabase
        .from('hardware_orders')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (fetchError) throw fetchError;

      setOrders(data || []);
    } catch (err) {
      console.error('[useBilling] Error fetching hardware orders:', err);
      setError('Failed to fetch hardware orders');
      setOrders([]);
    } finally {
      setIsOrdersLoading(false);
    }
  }, [organizationId]);

  // ── Fetch module subscriptions ──────────────────────────────────────────────

  const fetchModuleSubscriptions = useCallback(async () => {
    if (!organizationId) {
      setModuleSubscriptions([]);
      setIsModuleSubscriptionsLoading(false);
      return;
    }

    try {
      setIsModuleSubscriptionsLoading(true);

      const { data, error: fetchError } = await supabase
        .from('module_subscriptions')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setModuleSubscriptions(data || []);
    } catch (err) {
      console.error('[useBilling] Error fetching module subscriptions:', err);
      setModuleSubscriptions([]);
    } finally {
      setIsModuleSubscriptionsLoading(false);
    }
  }, [organizationId]);

  // ── Load data on mount / org change ────────────────────────────────────────

  useEffect(() => {
    fetchSubscription();
    fetchModuleSubscriptions();
    fetchInvoices();
    fetchOrders();
  }, [fetchSubscription, fetchModuleSubscriptions, fetchInvoices, fetchOrders]);

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Create a Stripe Checkout session for upgrading to a plan.
   * Returns the checkout URL to redirect the user to, or null on failure.
   */
  const createCheckoutSession = useCallback(
    async (
      plan: BillingPlan,
      billingInterval: 'monthly' | 'yearly' = 'monthly',
      potwierdz?: PotwierdzZmiany
    ): Promise<string | null> => {
      if (!organizationId) {
        setError('No organization selected');
        return null;
      }

      try {
        setError(null);

        // Build return URLs from current origin so Stripe redirects back
        // to the correct domain (Vercel preview, custom domain, localhost, etc.)
        const successUrl = `${window.location.origin}/admin?payment_success=true&plan=${plan}&session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = `${window.location.origin}/admin/billing`;

        const result = await zPotwierdzeniemZmian(
          (akceptuje) =>
            apiFetch<{ checkout_url: string; session_id: string }>(
              '/api/billing/create-checkout-session',
              organizationId,
              {
                method: 'POST',
                body: JSON.stringify({
                  plan,
                  billing_interval: billingInterval,
                  success_url: successUrl,
                  cancel_url: cancelUrl,
                  ...(akceptuje ? { akceptuje_zmiany_cennika: akceptuje } : {}),
                }),
              }
            ),
          potwierdz
        );

        return result?.checkout_url ?? null;
      } catch (err) {
        const message = (err as Error).message || 'Failed to create checkout session';
        console.error('[useBilling] createCheckoutSession error:', message);
        setError(message);
        return null;
      }
    },
    [organizationId]
  );

  /**
   * Create a Stripe Checkout session for purchasing a module.
   * Requires an active system subscription — returns error message if not.
   */
  const createModuleCheckoutSession = useCallback(
    async (params: {
      moduleId: string;
      tier: string;
      billingType?: string;
      currency?: string;
      /** Liczba sztuk — wyłącznie moduł licencjonowany ilościowo (Receptor), 1..20. */
      quantity?: number;
      /** Okno potwierdzenia ogłoszonej zmiany cennika (875). */
      potwierdz?: PotwierdzZmiany;
    }): Promise<string | null> => {
      if (!organizationId) {
        setError('No organization selected');
        return null;
      }

      try {
        setError(null);

        const successUrl = `${window.location.origin}/admin?payment_success=true&module=${params.moduleId}&session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = `${window.location.origin}/admin/modules`;

        const result = await zPotwierdzeniemZmian(
          (akceptuje) =>
            apiFetch<{ checkout_url: string; session_id: string }>(
              '/api/billing/create-module-checkout-session',
              organizationId,
              {
                method: 'POST',
                body: JSON.stringify({
                  module_id: params.moduleId,
                  tier: params.tier,
                  billing_type: params.billingType || 'monthly',
                  // Bez waluty: o walucie decyduje backend (waluta zamrożona
                  // w Stripe albo kraj organizacji) — front jej nie zgaduje.
                  ...(params.currency ? { currency: params.currency } : {}),
                  ...(params.quantity ? { ilosc: params.quantity } : {}),
                  success_url: successUrl,
                  cancel_url: cancelUrl,
                  ...(akceptuje ? { akceptuje_zmiany_cennika: akceptuje } : {}),
                }),
              }
            ),
          params.potwierdz
        );

        return result?.checkout_url ?? null;
      } catch (err) {
        const message = (err as Error).message || 'Failed to create module checkout session';
        console.error('[useBilling] createModuleCheckoutSession error:', message);
        setError(message);
        // Odmowa serwera (409 przy żywej subskrypcji — D-1, bramka zakupu)
        // niesie zdanie prowadzące do przycisku na karcie modułu. Okno zakupu
        // w `ModulesPage` NIE czyta `error` tego hooka, więc `null` gubił je
        // w konsoli — wyjątek trafia do `purchaseError` okna.
        throw new Error(message);
      }
    },
    [organizationId]
  );

  /**
   * Zmiana liczby serwerów modułu ilościowego (D10) — Stripe z proracją.
   * Licencję zapisuje webhook; odpowiedź mówi tylko, czy Stripe przyjął zmianę.
   */
  const changeModuleQuantity = useCallback(
    async (params: {
      moduleId: string;
      quantity: number;
    }): Promise<{ stan: string; ilosc: number; poprzednio: number }> => {
      if (!organizationId) throw new Error('No organization selected');
      return apiFetch<{ stan: string; ilosc: number; poprzednio: number }>(
        '/api/billing/module-quantity',
        organizationId,
        {
          method: 'POST',
          body: JSON.stringify({ module_id: params.moduleId, ilosc: params.quantity }),
        }
      );
    },
    [organizationId]
  );

  /**
   * Open the Stripe Customer Portal for self-service billing management.
   * Returns the portal URL to redirect the user to, or null on failure.
   */
  const openBillingPortal = useCallback(async (): Promise<string | null> => {
    if (!organizationId) {
      setError('No organization selected');
      return null;
    }

    try {
      setError(null);

      const returnUrl = `${window.location.origin}/settings/billing`;

      const result = await apiFetch<{ portal_url: string }>(
        '/api/billing/create-portal-session',
        organizationId,
        {
          method: 'POST',
          body: JSON.stringify({ return_url: returnUrl }),
        }
      );

      return result.portal_url;
    } catch (err) {
      const message = (err as Error).message || 'Failed to open billing portal';
      console.error('[useBilling] openBillingPortal error:', message);
      setError(message);
      return null;
    }
  }, [organizationId]);

  const cancelScheduledChange = useCallback(
    async (moduleId?: string): Promise<void> => {
      if (!organizationId) throw new Error('No organization selected');
      await apiFetch<{ stan: string }>('/api/billing/cancel-scheduled-change', organizationId, {
        method: 'POST',
        body: JSON.stringify(moduleId ? { module_id: moduleId } : {}),
      });
    },
    [organizationId]
  );

  /**
   * Refresh all billing data.
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchSubscription(),
      fetchModuleSubscriptions(),
      fetchInvoices(),
      fetchOrders(),
    ]);
  }, [fetchSubscription, fetchModuleSubscriptions, fetchInvoices, fetchOrders]);

  /**
   * Manually sync subscription from Stripe API.
   * Fixes missing subscription records after webhook failures.
   */
  const syncSubscription = useCallback(async (): Promise<boolean> => {
    if (!organizationId) {
      setError('No organization selected');
      return false;
    }

    try {
      setError(null);
      await apiFetch<{ synced: boolean }>('/api/billing/sync-subscription', organizationId, {
        method: 'POST',
      });

      // Refresh data after sync
      await fetchSubscription();
      return true;
    } catch (err) {
      const message = (err as Error).message || 'Failed to sync subscription';
      console.error('[useBilling] syncSubscription error:', message);
      setError(message);
      return false;
    }
  }, [organizationId, fetchSubscription]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const currentPlan: BillingPlan =
    subscription?.plan ||
    (currentOrganization?.organization?.subscription_plan as BillingPlan) ||
    'free';

  const hasActiveSystemSubscription =
    !!subscription &&
    ['active', 'trialing'].includes(subscription.status) &&
    ['starter', 'professional', 'enterprise'].includes(subscription.plan);

  /**
   * Otwórz kasę Stripe na sprzęt z Marketplace.
   *
   * Wysyłamy WYŁĄCZNIE slug i ilość. Kwotę wylicza backend z katalogu — cena
   * przekazana z przeglądarki byłaby ceną, którą kupujący może sobie wpisać.
   */
  const createDeviceCheckoutSession = useCallback(
    async (items: Array<{ slug: string; quantity: number }>): Promise<string | null> => {
      if (!organizationId) {
        setError('No organization selected');
        return null;
      }

      try {
        setError(null);
        const result = await apiFetch<{ checkout_url: string }>(
          '/api/marketplace/device-checkout-session',
          organizationId,
          {
            method: 'POST',
            body: JSON.stringify({
              items,
              success_url: `${window.location.origin}/admin/modules?zakup=ok&session_id={CHECKOUT_SESSION_ID}`,
              cancel_url: `${window.location.origin}/admin/modules?zakup=anulowany`,
            }),
          }
        );
        return result.checkout_url;
      } catch (err) {
        const message = (err as Error).message || 'Nie udało się otworzyć płatności';
        console.error('[useBilling] createDeviceCheckoutSession error:', message);
        setError(message);
        return null;
      }
    },
    [organizationId]
  );

  return {
    subscription,
    currentPlan,
    isSubscriptionLoading,
    hasActiveSystemSubscription,
    moduleSubscriptions,
    isModuleSubscriptionsLoading,
    invoices,
    isInvoicesLoading,
    orders,
    isOrdersLoading,
    error,
    maKlientaStripe,
    createCheckoutSession,
    createModuleCheckoutSession,
    changeModuleQuantity,
    createDeviceCheckoutSession,
    openBillingPortal,
    cancelScheduledChange,
    syncSubscription,
    refresh,
  };
}
